'use client';

import React from 'react';
import { styled } from '@linaria/react';
import { useSpring, animated } from 'react-spring';
import { ArrowRight, Star } from 'lucide-react';

import { BREAKPOINTS, TIGHT_SPRING } from '@/constants';
import usePrefersReducedMotion from '@/hooks/use-prefers-reduced-motion';

import {
  BaseWrapper as AsideWrapper,
  WarningAside,
} from '@/components/Aside';

type ListType = 'unordered' | 'ordered';
const ListContext = React.createContext<{ type: ListType }>({
  type: 'unordered',
});

const DefaultIcon = () => <ArrowRight size="1.25rem" />;
const FullStarIcon = () => <Star size="1.25rem" />;

const ICONS = {
  default: DefaultIcon,
  fullStar: FullStarIcon,
};

interface ListItemProps extends React.HTMLAttributes<HTMLLIElement> {
  bullet?: keyof typeof ICONS;
  animated?: boolean;
  children: React.ReactNode;
}

export const ListItem = ({
  bullet = 'default',
  animated = false,
  children,
  ...delegated
}: ListItemProps) => {
  const { type } = React.useContext(ListContext);

  const prefersReducedMotion = usePrefersReducedMotion();

  const IconComponent = ICONS[bullet];
  const [isHovering, setIsHovering] = React.useState(false);

  const iconSpring = useSpring(
    animated && !prefersReducedMotion
      ? {
          '--x': isHovering ? `8px` : `0px`,
          config: TIGHT_SPRING,
        }
      : { transform: 'translateY(4px) translateX(0px)' }
  );

  if (type === 'ordered') {
    return (
      <Wrapper {...delegated}>
        <ListItemContents>{children}</ListItemContents>
      </Wrapper>
    );
  }

  return (
    <Wrapper {...delegated}>
      <IconWrapper style={iconSpring}>
        <IconComponent />
      </IconWrapper>
      {/*
        This interaction is purely decorative, and is not required for
        folks using the keyboard or a screen-reader.
      */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <ListItemContents
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {children}
      </ListItemContents>
    </Wrapper>
  );
};

interface Props extends React.HTMLAttributes<HTMLUListElement> {
  type?: ListType;
}

const List = ({
  type = 'unordered',
  style = {},
  ...delegated
}: Props) => {
  // I kept running into an annoying issue where ordered lists would sometimes wind up with nested bullets (eg. "3.1." instead of "1."). This is because all lists shared the same `counter-reset` \ `counter-increment`. I solved this by giving each <ol> its own unique counter name, using `useId`:
  const id = React.useId().replace(/:/g, '');
  const counterName = `counts-${id}`;

  return (
    <ListContext.Provider value={{ type }}>
      {type === 'ordered' && (
        <OrderedListElem
          style={{ ...style, '--counter-name': counterName }}
          {...delegated}
        />
      )}
      {type === 'unordered' && <UnorderedListElem {...delegated} />}
    </ListContext.Provider>
  );
};

const UnorderedListElem = styled.ul`
  font-size: inherit;
  margin-bottom: 2rem;
  list-style: none;
  padding: 0;

  /* For nested list items, add top margin as well */
  ul & {
    margin-top: 16px;
  }

  @media ${BREAKPOINTS.smAndSmaller} {
    margin-bottom: 1.5rem;
  }
`;

const OrderedListElem = styled.ol`
  --counter-name: counts;

  font-size: inherit;
  margin-bottom: 2rem;
  counter-reset: var(--counter-name);
  list-style: none;
  padding: 0;

  & li {
    counter-increment: var(--counter-name);
    align-items: baseline;
  }

  & li::before {
    content: counters(var(--counter-name), '.') '. ';
    display: inline-flex;
    justify-content: center;
    font-feature-settings: 'tnum';
    color: var(--color-primary);
    font-weight: var(--font-weight-medium);
    padding-right: 12px;
    width: 2.25rem;
  }

  /* The orange warning color is too light, not enough contrast. Reset to text color. */
  ${WarningAside} & li::before {
    color: var(--color-text);
  }

  @media ${BREAKPOINTS.smAndSmaller} {
    font-size: calc(18 / 16 * 1rem);
    margin-bottom: 1.5rem;
  }
`;

const Wrapper = styled.li`
  margin-bottom: 16px;
  display: flex;
  align-items: flex-start;
  text-wrap: pretty;

  &:last-of-type {
    margin-bottom: 0;
  }

  ${AsideWrapper} & {
    font-size: 1rem;
    margin-bottom: 8px;
  }
`;

const ListItemContents = styled.div`
  flex: 1;
`;

const IconWrapper = styled(animated.span)`
  --optical-align: 0.1875em;
  display: flex;
  align-items: center;
  padding-right: 16px;
  padding-top: 0;
  color: var(--color-primary);
  transform: translate(var(--x), var(--optical-align));

  ${AsideWrapper} & {
    /* Optical alignment - text is smaller in asides, so the icon needs to be shifted */
    --optical-align: 0.125em;
  }

  ${WarningAside} & {
    color: var(--color-text);
  }
`;

List.ListItem = ListItem;

export default List;
